import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const params = useParams();
  const lang = params.lang || 'ru';
  const queryParams = new URLSearchParams(window.location.search);
  const invoiceIdFromUrl = queryParams.get('invoice_id');
  const amountFromUrl = queryParams.get('amount');
  const invoiceIdFromStorage = sessionStorage.getItem('pending_invoice_id');
  const invoiceId = invoiceIdFromUrl || invoiceIdFromStorage;

  const [status, setStatus] = useState('checking'); // checking | paid | failed | timeout
  const [orderId, setOrderId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const pollCountRef = useRef(0);
  const MAX_POLLS = 60; // 60 * 3s = 3 min

  useEffect(() => {
    if (!invoiceId) {
      setStatus('failed');
      setErrorMessage('Не найден идентификатор платежа');
      return;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      pollCountRef.current += 1;
      try {
        const r = await axios.get(`${API}/api/payments/status/${invoiceId}`, { headers: authHeaders() });
        if (cancelled) return;
        const s = r.data.status;

        if (s === 'paid') {
          // Commit the order if not already committed
          if (!r.data.order_id) {
            try {
              const commit = await axios.post(
                `${API}/api/payments/commit-order/${invoiceId}`, {},
                { headers: authHeaders() }
              );
              if (cancelled) return;
              setOrderId(commit.data.order_id);
              setStatus('paid');
              sessionStorage.removeItem('pending_invoice_id');
              sessionStorage.removeItem('pending_cart');
              return;
            } catch (commitErr) {
              console.error('commit-order failed', commitErr);
              // Keep polling — webhook might still be in flight
            }
          } else {
            setOrderId(r.data.order_id);
            setStatus('paid');
            sessionStorage.removeItem('pending_invoice_id');
            sessionStorage.removeItem('pending_cart');
            return;
          }
        } else if (s === 'failed' || s === 'cancelled' || s === 'expired') {
          setStatus('failed');
          setErrorMessage(r.data.error_message || 'Платёж не прошёл');
          return;
        }
      } catch (e) {
        console.warn('status poll failed', e);
      }

      if (pollCountRef.current >= MAX_POLLS) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(poll, 3000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [invoiceId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="payment-success-card">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Откройте Kaspi на телефоне</h1>
            <p className="text-gray-600 mb-2">
              Вам пришло push-уведомление об оплате
              {amountFromUrl ? <span className="font-bold text-emerald-700"> {amountFromUrl} ₸</span> : ''}.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Откройте Kaspi → подтвердите платёж. Эта страница автоматически обновится.
            </p>
            <div className="mt-6 p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
              Не закрывайте страницу. Если уведомление не пришло за 2 минуты — проверьте номер телефона в профиле.
            </div>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Оплата прошла!</h1>
            <p className="text-gray-600 mb-6">Заказ создан. Спасибо!</p>
            <Button
              className="w-full bg-emerald-700 hover:bg-emerald-800"
              onClick={() => navigate(`/${lang}/orders${orderId ? `/${orderId}` : ''}`)}
              data-testid="goto-orders-btn"
            >
              К моим заказам <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Платёж не прошёл</h1>
            <p className="text-gray-600 mb-6">{errorMessage || 'Попробуйте оформить заказ ещё раз.'}</p>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate(`/${lang}/catalog`)}
              data-testid="back-to-cart-btn"
            >
              Вернуться в каталог
            </Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-amber-500 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Долго обрабатывается</h1>
            <p className="text-gray-600 mb-6">
              Платёж может прийти позже. Проверьте «Мои заказы» через несколько минут.
            </p>
            <Button
              className="w-full bg-emerald-700 hover:bg-emerald-800"
              onClick={() => navigate(`/${lang}/orders`)}
            >
              Перейти в заказы
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
